import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import AuthLayout from './components/AuthLayout';
import { useLogin } from './api/auth';
import { ValidationErrors } from './types';

export default function Login() {
  const navigate = useNavigate();
  const { mutateAsync: login, isPending } = useLogin();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<ValidationErrors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    try {
      await login(formData);
      navigate('/dashboard'); // Or wherever your secure route is
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string; errors: ValidationErrors }>;
      
      // Handle Laravel's 422 Validation Errors
      if (axiosError.response?.status === 422) {
        setErrors(axiosError.response.data.errors || {});
        toast.error(axiosError.response.data.message || 'Please check the form for errors.');
      }
    }
  };

  return (
    <AuthLayout 
      title="Welcome back" 
      subtitle="Enter your details to access your account."
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <Input 
          label="Email address" 
          type="email" 
          placeholder="you@company.com" 
          required 
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          error={errors.email?.[0]}
        />
        
        <div>
          <Input 
            label="Password" 
            type="password" 
            placeholder="••••••••" 
            required 
            value={formData.password}
            onChange={(e) => setFormData({...formData, password: e.target.value})}
            error={errors.password?.[0]}
          />
          <div className="flex justify-end mt-2">
            <Link to="/forgot-password" className="text-sm font-semibold text-brand-700 hover:text-brand-900 transition-colors">
              Forgot password?
            </Link>
          </div>
        </div>

        <Button type="submit" fullWidth isLoading={isPending}>
          Log in to your account
        </Button>
      </form>

      {/* Social Auth Separator */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-slate-500">Or continue with</span>
          </div>
        </div>
        
        <div className="mt-6">
          <Button variant="outline" fullWidth type="button">
            <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" className="w-5 h-5 mr-2" />
            Google
          </Button>
        </div>
      </div>

      <p className="mt-8 text-center text-sm text-slate-600">
        Don't have an account?{' '}
        <Link to="/register" className="font-semibold text-brand-700 hover:text-brand-900 transition-colors">
          Sign up for free
        </Link>
      </p>
    </AuthLayout>
  );
}